import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import Button from '@rio-cloud/rio-uikit/lib/es/Button';
import Notification from '@rio-cloud/rio-uikit/lib/es/Notification';
import { FormattedMessage, useIntl } from 'react-intl';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { DeviceListFilter } from './DeviceList';
import { resetDeviceStates } from '../../../appSlice';
import {
    ACCOUNT_OR_DEVICE_ID_LENGTH,
    SERIAL_NUMBER_LENGTH,
    useLazyFetchLastMileageQuery,
    VIN_LENGTH,
} from '../../../../../services/deviceStateApi';
import {Status} from '../../device-sidebar/device-state.types';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import ToggleButton from '@rio-cloud/rio-uikit/lib/es/ToggleButton';
import Collapse from '@rio-cloud/rio-uikit/lib/es/Collapse';

interface DeviceListTableSearchProps {
    filter: DeviceListFilter;
    setFilter: Function;
    setShowTableSettingsDialog: Function;
    isLoading: boolean;
}

export const DeviceListTableSearch = (props: DeviceListTableSearchProps) => {
    const {formatMessage} = useIntl();
    const dispatch = useDispatch();
    const [searchPrefix, setSearchPrefix] = useState('');
    const [searchVin, setSearchVin] = useState('');
    const [accountIdPrefix, setAccountIdPrefix] = useState('');
    const [trigger] = useLazyFetchLastMileageQuery();
    const [show, setShow] = useState(false);
    const [resultMessage, setResultMessage] = useState<string | null>(null);

    const handleSearchVsrVinChange = (value: string) => {
        setSearchVin(value.trim());
        setResultMessage(null);
    };

    const clearSearchVsrVinChange = () => {
        setSearchVin('');
        setResultMessage(null);
    };

    const handleSearchValueChange = (searchPrefixValue: string) => {
        if (searchPrefixValue.trim().length === 0 && accountIdPrefix.length === 0) {
            dispatch(resetDeviceStates());
        }
        setSearchPrefix(searchPrefixValue.trim());
    };

    const handleAccountIdValueChange = (accountIdPrefixValue: string) => {
        if (accountIdPrefixValue.trim().length === 0 && searchPrefix.length === 0) {
            dispatch(resetDeviceStates());
        }
        setAccountIdPrefix(accountIdPrefixValue.trim());
    };

    const handleWithoutMileageChange = () => {
        dispatch(resetDeviceStates());
        props.setFilter({
            ...props.filter,
            withoutMileage: !props.filter.withoutMileage
        });
    };

    const handleStatusFilter = (status: Status) => {
        dispatch(resetDeviceStates());
        props.setFilter({
            ...props.filter,
            status: props.filter.status.valueOf() === status.valueOf() ? Status.EMPTY : status
        });
    };

    const handleSearchByPrefix = () => {
        dispatch(resetDeviceStates());
        props.setFilter({
            ...props.filter,
            serialNumberPrefix: searchPrefix.substring(0, SERIAL_NUMBER_LENGTH),
            deviceIdPrefix: searchPrefix.substring(0, ACCOUNT_OR_DEVICE_ID_LENGTH),
            vinPrefix: searchPrefix.substring(0, VIN_LENGTH),
            accountIdPrefix: accountIdPrefix.substring(0, ACCOUNT_OR_DEVICE_ID_LENGTH),
        });
    };

    return (
        <TableToolbar>
            <div className="table-toolbar-container">
                <div className="table-toolbar-group-left">
                    <div className="col-8">
                        <TableSearch
                            value={searchPrefix}
                            onChange={handleSearchValueChange}
                            placeholder={formatMessage({id: 'intl-msg:coreconAdminWeb.searchSerialOrVin'})}
                        />
                    </div>
                    <div className="col-4 padding-left-5">
                        <TableSearch
                            value={accountIdPrefix}
                            onChange={handleAccountIdValueChange}
                            placeholder={formatMessage({id: 'intl-msg:coreconAdminWeb.searchAccountId'})}/>
                    </div>
                    <div className="padding-left-5">
                        <Button active={!props.isLoading}
                                disabled={props.isLoading} onClick={handleSearchByPrefix}>
                            <FormattedMessage id={'intl-msg:coreconAdminWeb.search'}/>
                        </Button>
                    </div>
                </div>
                <div className="table-toolbar-group-right">
                    <button disabled className="btn btn-default margin-right-5">
                        <span className="rioglyph rioglyph-download"/>
                        CSV
                    </button>
                    <button className="btn btn-default btn-icon-only"
                            onClick={() => props.setShowTableSettingsDialog(true)}>
                        <span className="rioglyph rioglyph-settings"/>
                    </button>
                </div>
            </div>
            <div className="padding-top-10">
                <Button className="margin-right-10" active={props.filter.withoutMileage}
                        onClick={handleWithoutMileageChange}>
                    <FormattedMessage id={'intl-msg:coreconAdminWeb.withoutMileage'}/>
                </Button>
                <Button className="margin-right-10" active={props.filter.status === Status.ONLINE &&
                    props.filter.status === Status.ONLINE}
                        onClick={() => handleStatusFilter(Status.ONLINE)}>
                    <FormattedMessage id={'intl-msg:coreconAdminWeb.online'}/>
                </Button>
                <Button className="margin-right-10" active={props.filter.status === Status.OFFLINE &&
                    props.filter.status === Status.OFFLINE}
                        onClick={() => handleStatusFilter(Status.OFFLINE)}>
                    <FormattedMessage id={'intl-msg:coreconAdminWeb.offline'}/>
                </Button>
                <Button className="margin-right-10" active={props.filter.status === Status.INACTIVE &&
                    props.filter.status === Status.INACTIVE}
                        onClick={() => handleStatusFilter(Status.INACTIVE)}>
                    <FormattedMessage id={'intl-msg:coreconAdminWeb.inactive'}/>
                </Button>
            </div>
            <hr/>
            <FormLabel>Search for an OCU3 VIN to retrieve the last known mileage.</FormLabel>
            <div>
                {/* eslint-disable-next-line max-len */}
                <ToggleButton className='' active={show} onClick={() => setShow(!show)}>
                    {'OCU3 VSR Mileage'}
                </ToggleButton>
                <Collapse
                    open={show}
                    onExited={() => clearSearchVsrVinChange()}
                >
                    <div className="padding-left-5 row display-flex">
                        <div className="margin-top-10">
                            <div className="panel-wrapper">
                                <div className="panel panel-default">
                                    <div className="panel-body">
                                        <div className="padding-top-10 table-toolbar-container">
                                            <div className="table-toolbar-group-left">
                                                <div className="d-inline-block">
                                                    <TableSearch
                                                        value={searchVin}
                                                        onChange={handleSearchVsrVinChange}
                                                        /* eslint-disable-next-line max-len */
                                                        placeholder={formatMessage({ id: 'intl-msg:coreconAdminWeb.searchOCU3Vin' })}
                                                    />
                                                </div>
                                                <div className="padding-left-5 d-inline-block">
                                                    <Button
                                                        active={!props.isLoading}
                                                        disabled={props.isLoading || searchVin === ''}
                                                        onClick={() => {
                                                            trigger(searchVin)
                                                                .unwrap()
                                                                .then((data) => {
                                                                    // eslint-disable-next-line max-len
                                                                    setResultMessage(`Mileage fetched successfully: ${searchVin} - ${data.mileage}`);
                                                                })
                                                                .catch((error) => {
                                                                    // eslint-disable-next-line max-len
                                                                    setResultMessage(`Failed to fetch mileage\n\n${error.data.detail}`);
                                                                });
                                                        }}
                                                    >
                                                        <FormattedMessage id={'intl-msg:coreconAdminWeb.search'} />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        {resultMessage && (
                                            // eslint-disable-next-line max-len
                                            <div className="margin-top-10" style={{ whiteSpace: 'pre-wrap', color: resultMessage.startsWith('Mileage') ? 'green' : 'red' }}>
                                                {resultMessage}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>
        </TableToolbar>
    );
};
